import { useEffect, useState, useCallback, isValidElement } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { ArrowLeft, Download, ExternalLink, FileText } from "lucide-react"
import { fr, paymentTypeLabel, subscriptionStatusLabel } from "../../locales/fr"
import { getSubscription, downloadSubscriptionInvoice } from "../../services/subscriptionService"
import { downloadPaymentInvoice } from "../../services/paymentService"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"

const cardClass =
  "border border-slate-700 bg-slate-900 text-slate-100 shadow-sm [&_.text-muted-foreground]:text-slate-400"

function InfoRow({ label, value }) {
  const display = isValidElement(value)
    ? value
    : value != null && String(value).trim() !== ""
      ? value
      : "—"
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-slate-700/60 py-3 last:border-0 sm:grid-cols-3 sm:items-center">
      <dt className="text-sm font-medium text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-100 sm:col-span-2">{display}</dd>
    </div>
  )
}

function statusBadge(status) {
  const key = String(status || "").toLowerCase()
  const variant =
    key === "active" ? "success" : key === "expired" ? "destructive" : "secondary"
  return <Badge variant={variant}>{subscriptionStatusLabel(status)}</Badge>
}

export default function SubscriptionView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const d = fr.subscriptions.detail
  const [sub, setSub] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [downloadError, setDownloadError] = useState("")

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    setError("")
    getSubscription(id)
      .then(setSub)
      .catch(() => {
        setSub(null)
        setError(fr.subscriptions.detail.loadError)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
        {fr.common.loading}
      </div>
    )
  }

  if (error || !sub) {
    return (
      <div className="mx-auto max-w-lg space-y-4 text-center text-slate-100">
        <p className="text-slate-300">{error || d.notFound}</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/subscriptions")}>
          <ArrowLeft className="h-4 w-4" />
          {d.back}
        </Button>
      </div>
    )
  }

  const memberId = sub.member?.id
  const payments = Array.isArray(sub.payments) ? sub.payments : []
  const t = fr.tables

  return (
    <div className="mx-auto max-w-4xl space-y-6 text-slate-100">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate("/subscriptions")}>
          <ArrowLeft className="h-4 w-4" />
          {d.back}
        </Button>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500 sm:max-w-xs sm:text-right">{d.invoiceHint}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!sub.canDownloadInvoicePdf}
            className="gap-2 border-slate-600 text-slate-100 hover:bg-slate-800 disabled:opacity-50"
            onClick={async () => {
              setDownloadError("")
              try {
                await downloadSubscriptionInvoice(sub.id)
              } catch {
                setDownloadError(fr.invoice.downloadError)
              }
            }}
          >
            <Download className="h-4 w-4 shrink-0" />
            {d.downloadInvoice}
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-50">
          {d.title} #{sub.id}
        </h1>
        <p className="mt-1 text-sm text-slate-400">{d.subtitle}</p>
      </div>

      {downloadError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-300">
          {downloadError}
        </div>
      ) : null}

      {!sub.canDownloadInvoicePdf ? (
        <p className="text-sm text-amber-200/90">{d.invoiceUnavailable}</p>
      ) : null}

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.pricingSection}</CardTitle>
          <CardDescription className="!text-slate-400">
            {sub.pack} — {sub.plan}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <dl>
            <InfoRow label={fr.subscriptions.table.status} value={statusBadge(sub.status)} />
            <InfoRow label={fr.subscriptions.table.startDate} value={sub.startDate} />
            <InfoRow label={fr.subscriptions.table.endDate} value={sub.endDate} />
            <InfoRow label={fr.subscriptions.table.price} value={`${sub.price} MAD`} />
            <InfoRow label={fr.memberDetail.subscriptionPaidCol} value={`${sub.totalPaid} MAD`} />
            <InfoRow label={fr.memberDetail.subscriptionRemainingCol} value={`${sub.remaining} MAD`} />
          </dl>
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{fr.payments.detail.memberSection}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-lg font-medium text-slate-100">{sub.member?.name || "—"}</p>
          {memberId ? (
            <Link
              to={`/members/${memberId}`}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-slate-600 px-3 text-sm font-medium text-slate-100 transition-colors hover:bg-slate-800"
            >
              <ExternalLink className="h-4 w-4 shrink-0" />
              {fr.payments.detail.memberLink}
            </Link>
          ) : null}
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.paymentsSection}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:px-6 sm:pb-6 [&_thead]:border-slate-700 [&_thead]:bg-slate-800/80">
          {payments.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">{d.invoiceUnavailable}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-right text-slate-200">{t.amount}</TableHead>
                    <TableHead className="text-slate-200">{t.paymentType}</TableHead>
                    <TableHead className="text-slate-200">{t.date}</TableHead>
                    <TableHead className="text-slate-200">{fr.memberDetail.invoiceNumberCol}</TableHead>
                    <TableHead className="text-right text-slate-200">{t.invoice}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id} className="border-slate-700 hover:bg-slate-800/40">
                      <TableCell className="text-right font-medium text-emerald-400">
                        {p.amount} MAD
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {paymentTypeLabel(p.paymentType)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-300">{p.paidAt}</TableCell>
                      <TableCell className="font-mono text-sm text-slate-300">
                        {p.invoiceNumber || `REF-${p.id}`}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-slate-300 hover:bg-slate-800"
                            onClick={() => navigate(`/payments/${p.id}`)}
                          >
                            {d.openPayment}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-slate-600 text-slate-100"
                            title={fr.invoice.downloadPdf}
                            onClick={async () => {
                              try {
                                await downloadPaymentInvoice(p.id)
                              } catch {
                                setDownloadError(fr.invoice.downloadError)
                              }
                            }}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
